<template>
    <div id="medsync-queue-container">
        <card>
            <template #header>
                <div class="d-flex align-items-center justify-content-between">
                    <h1 class="card-title mb-0">MedSync Upcoming Anchor Dates</h1>
                    <b-button
                        variant="primary"
                        @click="loadQueue()"
                        :disabled="loading"
                        v-b-tooltip.hover
                        :title="lastUpdated ? `Last updated: ${formatDateTime(lastUpdated)}` : 'Refresh'"
                    >
                        <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
                    </b-button>
                </div>
            </template>

            <!-- Filters Row 1 -->
            <b-row class="mb-2 align-items-end">
                <b-col cols="12" lg="3">
                    <label class="filter-label">Search</label>
                    <b-form-input
                        v-model="searchTerm"
                        type="search"
                        placeholder="Patient Name or ID..."
                        debounce="300"
                    ></b-form-input>
                </b-col>
                <b-col cols="12" lg="2">
                    <label class="filter-label">Days Ahead</label>
                    <b-form-select v-model="daysAhead" :options="daysAheadOptions" @change="loadQueue()"></b-form-select>
                </b-col>
                <b-col cols="12" lg="2">
                    <label class="filter-label">Priority</label>
                    <b-form-select v-model="priorityFilter" :options="priorityOptions"></b-form-select>
                </b-col>
                <b-col cols="12" lg="2">
                    <label class="filter-label"># of Meds</label>
                    <b-form-select v-model="medicationCountFilter" :options="medicationCountOptions"></b-form-select>
                </b-col>
                <b-col cols="12" lg="3">
                    <label class="filter-label">Contact Status</label>
                    <b-form-select v-model="contactStatusFilter" :options="contactStatusOptions" @change="loadQueue"></b-form-select>
                </b-col>
            </b-row>

            <!-- Filters Row 2 -->
            <b-row class="mb-3 align-items-end">
                <b-col cols="12" lg="3">
                    <label class="filter-label">Last Contact</label>
                    <b-form-select v-model="lastContactDateFilter" :options="lastContactDateOptions"></b-form-select>
                </b-col>
                <b-col cols="12" lg="3">
                    <label class="filter-label">Assigned To</label>
                    <b-form-select v-model="assignedToFilter" :options="assignedToOptions"></b-form-select>
                </b-col>
            </b-row>

            <!-- Queue Table -->
            <b-table
                :items="filteredQueueItems"
                :fields="fields"
                :busy="loading"
                striped
                show-empty
                empty-text="No patients with upcoming anchor dates found"
                sort-by="daysUntilAnchor"
                :sort-desc="false"
                @row-clicked="toggleRowSelection"
                :tbody-tr-class="getRowClass"
            >
                <!-- Select All Checkbox Header -->
                <template #head(select)>
                    <b-checkbox
                        :checked="isAllSelected"
                        :indeterminate="isSomeSelected"
                        @change="toggleSelectAll"
                        :disabled="!filteredQueueItems.length"
                    />
                </template>

                <!-- Row Checkbox -->
                <template #cell(select)="data">
                    <b-checkbox
                        :value="data.item"
                        v-model="selectedItems"
                        @click.native.stop="handleCheckboxClick($event, data.item, data.index)"
                    />
                </template>

                <!-- Expand Chevron -->
                <template #cell(expand)="data">
                    <i
                        class="fa fa-chevron-right expand-chevron"
                        :class="{ 'expanded': data.detailsShowing }"
                        @click.stop="toggleMedicationDetails(data)"
                    ></i>
                </template>

                <!-- Priority Badge -->
                <template #cell(priority)="data">
                    <b-badge :variant="getPriorityVariant(data.item.priority)" class="badge-block">
                        {{ data.item.priority }}
                    </b-badge>
                </template>

                <!-- Patient Name with link -->
                <template #cell(patientName)="data">
                    <router-link :to="`/processing/patient-dashboard/${data.item.patientId}`">
                        {{ data.item.patientName }}
                    </router-link>
                </template>

                <!-- Anchor Date formatted -->
                <template #cell(anchorDate)="data">
                    {{ formatDate(data.item.anchorDate) }}
                </template>

                <!-- Days Until Anchor with color coding -->
                <template #cell(daysUntilAnchor)="data">
                    <span :class="getDaysUntilClass(data.item.daysUntilAnchor)">
                        {{ data.item.daysUntilAnchor }}
                    </span>
                </template>

                <!-- Needs New Rx with warning if > 0 -->
                <template #cell(needsNewRxCount)="data">
                    <span :class="{ 'text-warning font-weight-bold': data.item.needsNewRxCount > 0 }">
                        {{ data.item.needsNewRxCount }}
                    </span>
                </template>

                <!-- Contact Status -->
                <template #cell(contactStatus)="data">
                    <b-badge :variant="getContactStatusVariant(data.item.contactStatus)" class="badge-block">
                        {{ data.item.contactStatus }}
                    </b-badge>
                </template>

                <!-- Assigned To -->
                <template #cell(assignedToDisplayName)="data">
                    <span v-if="data.item.assignedToDisplayName">
                        {{ data.item.assignedToDisplayName }}
                    </span>
                    <span v-else class="text-muted">Unassigned</span>
                </template>

                <!-- Last Contact Date -->
                <template #cell(lastContactDate)="data">
                    {{ data.item.lastContactDate ? formatDate(data.item.lastContactDate) : 'Never' }}
                </template>

                <!-- Actions -->
                <template #cell(actions)="data">
                    <div class="d-flex align-items-center">
                        <b-dropdown size="sm" variant="info" text="Assign" class="mr-1">
                            <b-dropdown-item @click="assignToMe(data.item)">
                                <i class="fa fa-fw fa-user mr-1"></i> Assign to Me
                            </b-dropdown-item>
                            <b-dropdown-item @click="openAssignModal(data.item)">
                                <i class="fa fa-fw fa-users mr-1"></i> Assign...
                            </b-dropdown-item>
                        </b-dropdown>
                        <b-button size="sm" variant="success" class="action-btn" @click="openContactModal(data.item)">
                            Contact Patient
                        </b-button>
                    </div>
                </template>

                <!-- Row Details for Medications and Contact History -->
                <template #row-details="data">
                    <b-card>
                        <!-- Medications Section -->
                        <b-row class="mb-3">
                            <b-col>
                                <h5>Medications</h5>
                                <b-row v-if="loadingMedications[data.item.enrollmentId]">
                                    <b-col class="text-center">
                                        <b-spinner small></b-spinner> Loading medications...
                                    </b-col>
                                </b-row>
                                <b-row v-else-if="medications[data.item.enrollmentId] && medications[data.item.enrollmentId].length > 0">
                                    <b-col>
                                        <b-table
                                            :items="medications[data.item.enrollmentId]"
                                            :fields="medicationFields"
                                            striped
                                            small
                                        >
                                            <!-- Drug Name -->
                                            <template #cell(drugName)="medData">
                                                {{ medData.item.drugName }}
                                            </template>

                                            <!-- Day Supply -->
                                            <template #cell(daySupply)="medData">
                                                {{ medData.item.daySupply || 'N/A' }}
                                            </template>

                                            <!-- Refills Remaining with warning -->
                                            <template #cell(refillsRemaining)="medData">
                                                <span :class="{ 'text-danger font-weight-bold': medData.item.refillsRemaining === 0 }">
                                                    {{ medData.item.refillsRemaining ?? 'N/A' }}
                                                </span>
                                            </template>
                                        </b-table>
                                    </b-col>
                                </b-row>
                                <b-row v-else>
                                    <b-col class="text-center text-muted">
                                        No medications found
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>

                        <!-- Contact History Section -->
                        <b-row>
                            <b-col>
                                <h5>Contact History</h5>
                                <b-row v-if="loadingContactHistory[data.item.enrollmentId]">
                                    <b-col class="text-center">
                                        <b-spinner small></b-spinner> Loading contact history...
                                    </b-col>
                                </b-row>
                                <b-row v-else-if="contactHistory[data.item.enrollmentId] && contactHistory[data.item.enrollmentId].length > 0">
                                    <b-col>
                                        <b-table
                                            :items="contactHistory[data.item.enrollmentId]"
                                            :fields="contactHistoryFields"
                                            striped
                                            small
                                        >
                                            <!-- Contact Date -->
                                            <template #cell(contactedDate)="histData">
                                                {{ formatDate(histData.item.contactedDate) }}
                                            </template>

                                            <!-- Contact Status -->
                                            <template #cell(contactStatus)="histData">
                                                <b-badge :variant="getContactStatusVariant(histData.item.contactStatus)">
                                                    {{ histData.item.contactStatus }}
                                                </b-badge>
                                            </template>

                                            <!-- Notes -->
                                            <template #cell(notes)="histData">
                                                {{ histData.item.notes || '-' }}
                                            </template>
                                        </b-table>
                                    </b-col>
                                </b-row>
                                <b-row v-else>
                                    <b-col class="text-center text-muted">
                                        No contact history
                                    </b-col>
                                </b-row>
                            </b-col>
                        </b-row>

                        <!-- Notes Section -->
                        <b-row class="mt-3">
                            <b-col>
                                <h5>Notes</h5>
                                <NotesList
                                    :entity-id="data.item.enrollmentId"
                                    :entity-type="EntityType.MedSyncEnrollment"
                                    :page-size="10"
                                    empty-text="No notes on this MedSync enrollment yet."
                                    @note-added="data.item.noteCount++"
                                    @note-deleted="data.item.noteCount = Math.max(0, data.item.noteCount - 1)" />
                            </b-col>
                        </b-row>
                    </b-card>
                </template>

                <!-- Loading state -->
                <template #table-busy>
                    <div class="text-center my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong class="ml-2">Loading queue...</strong>
                    </div>
                </template>
            </b-table>

            <!-- Bulk Action Toolbar -->
            <BottomPagerBar
                :total-rows="filteredQueueItems.length"
                :selected-rows="selectedItems.length"
                item-name="patient"
                hide-left
                hide-right
                hide-pager
                @clear-selection="clearSelection"
            >
                <template #fab>
                    <b-dropdown v-if="selectedItems.length" variant="primary" text="Bulk Actions" size="sm" right dropup>
                        <b-dropdown-item @click="exportToCsv">
                            <i class="fa fa-file-csv mr-2"></i> Export to CSV
                        </b-dropdown-item>
                        <b-dropdown-item @click="generateCallList">
                            <i class="fa fa-phone-square mr-2"></i> Generate Call List
                        </b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="showBulkContactModal">
                            <i class="fa fa-phone mr-2"></i> Mark as Attempted...
                        </b-dropdown-item>
                        <b-dropdown-item @click="showAssignModal">
                            <i class="fa fa-user-plus mr-2"></i> Assign...
                        </b-dropdown-item>
                        <b-dropdown-divider></b-dropdown-divider>
                        <b-dropdown-item @click="showBatchReminderModal" :disabled="!canSendBatchReminder">
                            <i class="fa fa-envelope mr-2"></i> Send Batch Reminder...
                        </b-dropdown-item>
                    </b-dropdown>
                </template>
            </BottomPagerBar>
        </card>

        <!-- Contact Patient Modal -->
        <ContactPatientModal
            ref="contactModal"
            modal-id="contact-patient-modal"
            @contact-saved="onContactSaved"
            @hidden="onModalHidden"
        />

        <!-- Assign User Modal (Single) -->
        <b-modal
            id="assign-user-modal"
            title="Assign MedSync Enrollment"
            @ok="handleAssign"
            @hidden="resetAssignModal"
            ok-title="Assign"
            :ok-disabled="assigning"
        >
            <template v-if="assignModalItem">
                <p>
                    Assign <strong>{{ assignModalItem.patientName }}</strong>'s MedSync enrollment to:
                </p>
                <SearchComponent
                    v-model="singleAssignee"
                    label="User"
                    search-u-r-l="/User"
                    :prepopulate="true"
                    :clearable="true"
                    :option-label="formatUserLabel"
                />
                <p v-if="assignModalItem.assignedToDisplayName" class="mt-3 text-muted">
                    Currently assigned to: {{ assignModalItem.assignedToDisplayName }}
                </p>
            </template>
        </b-modal>

        <!-- Bulk Contact Modal -->
        <b-modal
            id="bulk-contact-modal"
            title="Mark Selected Patients as Contacted"
            @ok="saveBulkContact"
            :ok-disabled="!bulkContactStatus"
        >
            <p>You are about to log a contact attempt for <strong>{{ selectedItems.length }}</strong> {{ pluralize(selectedItems.length, 'patient') }}.</p>
            <b-form-group label="Contact Status">
                <b-form-select v-model="bulkContactStatus" :options="bulkContactStatusOptions"></b-form-select>
            </b-form-group>
            <b-form-group label="Notes (optional)">
                <b-form-textarea v-model="bulkContactNotes" class="textarea-autosize" rows="3" placeholder="Add notes for all selected patients..."></b-form-textarea>
            </b-form-group>
        </b-modal>

        <!-- Assign Modal (Bulk) -->
        <b-modal
            id="assign-modal"
            title="Assign Patients"
            @ok="saveAssignment"
            :ok-disabled="!selectedAssigneeUserName"
        >
            <p>Assign <strong>{{ selectedItems.length }}</strong> {{ pluralize(selectedItems.length, 'patient') }} to a user for follow-up.</p>
            <b-form-group label="Assign To">
                <SearchComponent
                    v-model="selectedAssignee"
                    search-u-r-l="/User"
                    label=""
                    :prepopulate="true"
                    :clearable="true"
                    :option-label="formatUserLabel"
                />
            </b-form-group>
        </b-modal>

        <!-- Batch Reminder Modal -->
        <b-modal
            id="batch-reminder-modal"
            title="Send Batch Reminder"
            @ok="sendBatchReminder"
            :ok-disabled="sendingReminder"
        >
            <p>Send a reminder to <strong>{{ selectedItems.length }}</strong> {{ pluralize(selectedItems.length, 'patient') }} about their upcoming MedSync fill date.</p>
            <b-form-group label="Reminder Type">
                <b-form-radio-group v-model="reminderType" :options="reminderTypeOptions" stacked></b-form-radio-group>
            </b-form-group>
            <b-alert variant="info" show>
                <i class="fa fa-info-circle mr-1"></i>
                Patients without a valid phone number or email will be skipped.
            </b-alert>
        </b-modal>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import Axios from 'axios';
import moment from 'moment';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import ContactPatientModal from '@/components/MedSync/ContactPatientModal.vue';
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import NotesList from '@/components/Note/NotesList.vue';
import { MedSyncQueueItem, MedSyncMedication, MedSyncContactHistory } from "@/models/MedSync";
import { User } from "@/models/User";
import { EntityType } from '@/models/EntityType';

@Component({
    name: 'MedSyncQueuePage',
    components: {
        ContactPatientModal,
        BottomPagerBar,
        SearchComponent,
        NotesList,
    },
})
export default class MedSyncQueuePage extends Vue {
    // Expose EntityType to the template (Vue class components can't reference imports directly in templates).
    private EntityType = EntityType;

    private queueItems: MedSyncQueueItem[] = [];
    private medications: { [enrollmentId: number]: MedSyncMedication[] } = {};
    private contactHistory: { [enrollmentId: number]: MedSyncContactHistory[] } = {};
    private loadingMedications: { [enrollmentId: number]: boolean } = {};
    private loadingContactHistory: { [enrollmentId: number]: boolean } = {};
    protected loading = false;
    private daysAhead = 14;
    private contactStatusFilter = 'All';
    private searchTerm = '';
    private priorityFilter = 'All';
    private medicationCountFilter = 'All';
    private lastContactDateFilter = 'All';
    private assignedToFilter = 'All';
    private refreshInterval: number | null = null;
    private lastUpdated: Date | null = null;
    private modalIsOpen = false;
    private previousQueueCount = 0;

    // Single assignment modal state
    private assignModalItem: MedSyncQueueItem | null = null;
    private singleAssignee: User = new User();
    private assigning = false;

    // Bulk selection state
    private selectedItems: MedSyncQueueItem[] = [];
    private lastSelectedIndex = -1;

    // Bulk contact modal state
    private bulkContactStatus = '';
    private bulkContactNotes = '';

    // Bulk assign modal state
    private selectedAssignee: User = new User();

    // Batch reminder state
    private reminderType = 'sms';
    private sendingReminder = false;

    protected daysAheadOptions =[
        { value: 7, text: '7 Days' },
        { value: 14, text: '14 Days' },
        { value: 21, text: '21 Days' },
        { value: 30, text: '30 Days' },
        { value: 999, text: 'All' },
    ];

    protected contactStatusOptions = [
        { value: 'All', text: 'All Statuses' },
        { value: 'Not Contacted', text: 'Not Contacted' },
        { value: 'Contacted', text: 'Contacted' },
        { value: 'Left Message', text: 'Left Message' },
        { value: 'No Answer', text: 'No Answer' },
        { value: 'Confirmed', text: 'Confirmed' },
        { value: 'Declined', text: 'Declined' },
    ];

    protected priorityOptions = [
        { value: 'All', text: 'All Priorities' },
        { value: 'High', text: 'High' },
        { value: 'Medium', text: 'Medium' },
        { value: 'Standard', text: 'Standard' },
    ];

    protected medicationCountOptions = [
        { value: 'All', text: 'All' },
        { value: '1-2', text: '1-2 Meds' },
        { value: '3-4', text: '3-4 Meds' },
        { value: '5+', text: '5+ Meds' },
    ];

    protected lastContactDateOptions = [
        { value: 'All', text: 'All' },
        { value: 'Never', text: 'Never Contacted' },
        { value: 'Today', text: 'Today' },
        { value: 'Last7Days', text: 'Last 7 Days' },
        { value: 'Last30Days', text: 'Last 30 Days' },
        { value: 'MoreThan30Days', text: 'More than 30 Days Ago' },
    ];

    protected assignedToOptions = [
        { value: 'All', text: 'All' },
        { value: 'Unassigned', text: 'Unassigned' },
        { value: 'AssignedToMe', text: 'Assigned to Me' },
        { value: 'Assigned', text: 'Assigned (Any)' },
    ];

    protected fields = [
        { key: 'select', label: '', sortable: false, thStyle: { width: '50px' } },
        { key: 'expand', label: '', sortable: false, thStyle: { width: '40px' } },
        { key: 'priority', label: 'Priority', sortable: true, thStyle: { width: '100px' } },
        { key: 'patientName', label: 'Patient Name', sortable: true },
        { key: 'patientId', label: 'Patient ID', sortable: true },
        { key: 'anchorDate', label: 'Anchor Date', sortable: true },
        { key: 'daysUntilAnchor', label: 'Days Until', sortable: true },
        { key: 'medicationCount', label: '# of Meds', sortable: true },
        { key: 'needsNewRxCount', label: 'Needs New Rx', sortable: true },
        { key: 'contactStatus', label: 'Contact Status', sortable: true },
        { key: 'assignedToDisplayName', label: 'Assigned To', sortable: true },
        { key: 'lastContactDate', label: 'Last Contact', sortable: true },
        { key: 'noteCount', label: 'Note Count', sortable: true, thStyle: { width: '110px' } },
        { key: 'actions', label: '', thStyle: { width: '280px' } },
    ];

    protected bulkContactStatusOptions = [
        { value: '', text: 'Select status...' },
        { value: 'Contacted', text: 'Contacted' },
        { value: 'Left Message', text: 'Left Message' },
        { value: 'No Answer', text: 'No Answer' },
    ];

    protected reminderTypeOptions = [
        { value: 'sms', text: 'SMS Text Message' },
        // TODO: Enable email options when email integration is ready
        // { value: 'email', text: 'Email' },
        // { value: 'both', text: 'Both SMS and Email' },
    ];

    protected medicationFields = [
        { key: 'drugName', label: 'Drug Name' },
        { key: 'rxID', label: 'Rx ID' },
        { key: 'daySupply', label: 'Days Supply', thClass: 'text-left', tdClass: 'text-left' },
        { key: 'refillsRemaining', label: 'Refills', thClass: 'text-left', tdClass: 'text-left' },
    ];

    protected contactHistoryFields = [
        { key: 'contactedDate', label: 'Date', sortable: true },
        { key: 'contactedBy', label: 'Contacted By', sortable: true },
        { key: 'contactStatus', label: 'Status', sortable: true },
        { key: 'notes', label: 'Notes' },
    ];

    created() {
        this.loadQueue();
        this.startAutoRefresh();
    }

    beforeDestroy() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
    }

    startAutoRefresh() {
        const FIVE_MINUTES = 5 * 60 * 1000;
        this.refreshInterval = setInterval(() => {
            if (!this.modalIsOpen && !this.loading) {
                this.loadQueue(true);
            }
        }, FIVE_MINUTES) as unknown as number;
    }

    // Client-side filtering for search, priority, medication count, last contact date, and assigned to
    // Contact status filtering is done server-side for performance
    get filteredQueueItems(): MedSyncQueueItem[] {
        let items = this.queueItems;

        // Search filter (Patient Name or Patient ID)
        if (this.searchTerm) {
            const search = this.searchTerm.toLowerCase();
            items = items.filter(item =>
                (item.patientName || '').toLowerCase().includes(search) ||
                item.patientId.toString().includes(search)
            );
        }

        // Priority filter
        if (this.priorityFilter !== 'All') {
            items = items.filter(item => item.priority === this.priorityFilter);
        }

        // Medication Count filter (ranges)
        if (this.medicationCountFilter !== 'All') {
            items = items.filter(item => {
                const count = item.medicationCount;
                switch (this.medicationCountFilter) {
                    case '1-2': return count >= 1 && count <= 2;
                    case '3-4': return count >= 3 && count <= 4;
                    case '5+': return count >= 5;
                    default: return true;
                }
            });
        }

        // Last Contact Date filter
        if (this.lastContactDateFilter !== 'All') {
            const now = moment();
            items = items.filter(item => {
                const lastContact = item.lastContactDate ? moment(item.lastContactDate) : null;

                switch (this.lastContactDateFilter) {
                    case 'Never':
                        return !lastContact;
                    case 'Today':
                        return lastContact && lastContact.isSame(now, 'day');
                    case 'Last7Days':
                        return lastContact && lastContact.isAfter(now.clone().subtract(7, 'days'));
                    case 'Last30Days':
                        return lastContact && lastContact.isAfter(now.clone().subtract(30, 'days'));
                    case 'MoreThan30Days':
                        return lastContact && lastContact.isBefore(now.clone().subtract(30, 'days'));
                    default:
                        return true;
                }
            });
        }

        // Assigned To filter
        if (this.assignedToFilter !== 'All') {
            const currentUser = this.$user?.userName;
            items = items.filter(item => {
                switch (this.assignedToFilter) {
                    case 'Unassigned':
                        return !item.assignedTo;
                    case 'AssignedToMe':
                        return item.assignedTo === currentUser;
                    case 'Assigned':
                        return !!item.assignedTo;
                    default:
                        return true;
                }
            });
        }

        return items;
    }

    get isAllSelected(): boolean {
        if (!this.filteredQueueItems.length) return false;
        return this.filteredQueueItems.every(item =>
            this.selectedItems.some(selected => selected.enrollmentId === item.enrollmentId)
        );
    }

    get isSomeSelected(): boolean {
        if (!this.filteredQueueItems.length) return false;
        const selectedCount = this.filteredQueueItems.filter(item =>
            this.selectedItems.some(selected => selected.enrollmentId === item.enrollmentId)
        ).length;
        return selectedCount > 0 && selectedCount < this.filteredQueueItems.length;
    }

    get selectedAssigneeUserName(): string | null {
        return this.selectedAssignee.userName || null;
    }

    get canSendBatchReminder(): boolean {
        // Check if the batch reminder feature is available (can be toggled via feature flag)
        return true;
    }

    async loadQueue(isAutoRefresh = false) {
        this.loading = true;
        try {
            const params: Record<string, any> = {
                daysAhead: this.daysAhead,
                // Send user's local date for accurate "days until anchor" calculation
                asOfDate: new Date().toLocaleDateString('en-CA')
            };
            // Only pass contactStatus if not 'All' (server-side filtering)
            if (this.contactStatusFilter && this.contactStatusFilter !== 'All') {
                params.contactStatus = this.contactStatusFilter;
            }
            const response = await Axios.get(`/api/medsync/upcoming-anchor-dates`, { params });
            const newItems: MedSyncQueueItem[] = response.data;

            // Check for new patients on auto-refresh
            if (isAutoRefresh && newItems.length > this.previousQueueCount) {
                const newCount = newItems.length - this.previousQueueCount;
                this.$notification(NotificationOptions.success(
                    `${newCount} new ${this.pluralize(newCount, 'patient')} added to MedSync queue`
                ));
            }

            this.queueItems = newItems;
            this.previousQueueCount = newItems.length;
            this.lastUpdated = new Date();
        } catch (error) {
            console.error('Error loading MedSync queue:', error);
            this.$notification(NotificationOptions.error('Failed to load MedSync queue'));
        } finally {
            this.loading = false;
        }
    }

    toggleMedicationDetails(rowData: any) {
        // Toggle the row details visibility
        rowData.toggleDetails();

        // If we're showing the details (not hiding), load the data
        if (!rowData.detailsShowing) {
            this.loadMedications(rowData.item.enrollmentId);
            this.loadContactHistory(rowData.item.enrollmentId);
        }
    }

    async loadMedications(enrollmentId: number) {
        if (this.medications[enrollmentId]) {
            return; // Already loaded
        }

        this.$set(this.loadingMedications, enrollmentId, true);
        try {
            const response = await Axios.get(`/api/medsync/enrollment/${enrollmentId}/medications`);
            this.$set(this.medications, enrollmentId, response.data);
        } catch (error) {
            console.error(`Error loading medications for enrollment ${enrollmentId}:`, error);
            this.$notification(NotificationOptions.error('Failed to load medications'));
        } finally {
            this.$set(this.loadingMedications, enrollmentId, false);
        }
    }

    async loadContactHistory(enrollmentId: number) {
        if (this.contactHistory[enrollmentId]) {
            return; // Already loaded
        }

        this.$set(this.loadingContactHistory, enrollmentId, true);
        try {
            const response = await Axios.get(`/api/medsync/enrollment/${enrollmentId}/contact-history`);
            this.$set(this.contactHistory, enrollmentId, response.data);
        } catch (error) {
            console.error(`Error loading contact history for enrollment ${enrollmentId}:`, error);
            this.$notification(NotificationOptions.error('Failed to load contact history'));
        } finally {
            this.$set(this.loadingContactHistory, enrollmentId, false);
        }
    }

    openContactModal(item: MedSyncQueueItem) {
        this.modalIsOpen = true;
        const modal = this.$refs.contactModal as any;
        if (modal && typeof modal.show === 'function') {
            modal.show(item);
        }
    }

    onContactSaved(enrollmentId: number) {
        this.modalIsOpen = false;

        // Clear the contact history cache for this enrollment so it reloads
        this.$delete(this.contactHistory, enrollmentId);

        // Refresh the queue to get updated contact status
        this.loadQueue();
    }

    onModalHidden() {
        this.modalIsOpen = false;
    }

    getPriorityVariant(priority: string): string {
        switch (priority) {
            case 'High':
                return 'danger';
            case 'Medium':
                return 'warning';
            case 'Standard':
                return 'success';
            default:
                return 'secondary';
        }
    }

    getContactStatusVariant(status: string): string {
        switch (status) {
            case 'Confirmed':
                return 'success';
            case 'Contacted':
                return 'primary';
            case 'Left Message':
                return 'info';
            case 'Not Contacted':
                return 'info';
            case 'No Answer':
                return 'warning';
            case 'Declined':
                return 'danger';
            case 'Patient Declined':
                return 'primary';
            default:
                return 'secondary';
        }
    }

    getDaysUntilClass(days: number): string {
        if (days <= 3) {
            return 'text-danger font-weight-bold';
        } else if (days <= 7) {
            return 'text-warning font-weight-bold';
        }
        return '';
    }

    formatDate(dateString: string): string {
        return moment.utc(dateString).local().format('MM/DD/YYYY');
    }

    formatDateTime(date: Date): string {
        // lastUpdated is already local time (from new Date()), no UTC conversion needed
        return moment(date).format('MM/DD/YYYY h:mm A');
    }

    pluralize(count: number, singular: string, plural?: string): string {
        return count === 1 ? singular : (plural || singular + 's');
    }

    formatUserLabel(user: User): string {
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName} (${user.userName})`;
        }
        return user.userName;
    }

    openAssignModal(item: MedSyncQueueItem) {
        this.assignModalItem = item;
        // Pre-populate with current assignee if any
        if (item.assignedTo) {
            this.singleAssignee = new User({ userName: item.assignedTo } as User);
        } else {
            this.singleAssignee = new User();
        }
        this.$bvModal.show('assign-user-modal');
    }

    resetAssignModal() {
        this.assignModalItem = null;
        this.singleAssignee = new User();
    }

    async handleAssign(bvModalEvent: Event) {
        bvModalEvent.preventDefault();

        if (!this.assignModalItem) return;

        this.assigning = true;
        try {
            await Axios.post(`/api/medsync/enrollment/${this.assignModalItem.enrollmentId}/assign`, {
                assignedTo: this.singleAssignee?.userName || null
            });

            this.$notification(NotificationOptions.successSaveNotificationPreset('Assignment'));
            this.$bvModal.hide('assign-user-modal');

            // Refresh the queue to show updated assignment
            await this.loadQueue();
        } catch (error) {
            console.error('Error assigning enrollment:', error);
            this.$notification(NotificationOptions.error('Failed to assign enrollment'));
        } finally {
            this.assigning = false;
        }
    }

    async assignToMe(item: MedSyncQueueItem) {
        const currentUser = this.$user?.userName;
        if (!currentUser) {
            this.$notification(NotificationOptions.error('Unable to determine current user'));
            return;
        }

        try {
            await Axios.post(`/api/medsync/enrollment/${item.enrollmentId}/assign`, {
                assignedTo: currentUser
            });

            this.$notification(NotificationOptions.successSaveNotificationPreset('Assignment'));
            await this.loadQueue();
        } catch (error) {
            console.error('Error self-assigning:', error);
            this.$notification(NotificationOptions.error('Failed to assign enrollment'));
        }
    }

    // Selection methods
    toggleRowSelection(item: MedSyncQueueItem, index?: number) {
        const selectedIndex = this.selectedItems.findIndex(selected => selected.enrollmentId === item.enrollmentId);
        if (selectedIndex > -1) {
            this.selectedItems.splice(selectedIndex, 1);
        } else {
            this.selectedItems.push(item);
        }

        if (index !== undefined) {
            this.lastSelectedIndex = index;
        }
    }

    handleCheckboxClick(event: MouseEvent, item: MedSyncQueueItem, index: number) {
        if (event.shiftKey && this.lastSelectedIndex !== -1 && this.lastSelectedIndex !== index) {
            event.preventDefault();

            if (window.getSelection) {
                window.getSelection()?.removeAllRanges();
            }

            const start = Math.min(this.lastSelectedIndex, index);
            const end = Math.max(this.lastSelectedIndex, index);

            for (let i = start; i <= end; i++) {
                const queueItem = this.filteredQueueItems[i];
                if (queueItem) {
                    const isSelected = this.selectedItems.some(selected => selected.enrollmentId === queueItem.enrollmentId);
                    if (!isSelected) {
                        this.selectedItems.push(queueItem);
                    }
                }
            }
        } else {
            this.lastSelectedIndex = index;
        }
    }

    toggleSelectAll() {
        if (this.isAllSelected) {
            // Deselect all filtered items
            this.filteredQueueItems.forEach(item => {
                const index = this.selectedItems.findIndex(selected => selected.enrollmentId === item.enrollmentId);
                if (index > -1) {
                    this.selectedItems.splice(index, 1);
                }
            });
        } else {
            // Select all filtered items
            this.filteredQueueItems.forEach(item => {
                const isSelected = this.selectedItems.some(selected => selected.enrollmentId === item.enrollmentId);
                if (!isSelected) {
                    this.selectedItems.push(item);
                }
            });
        }
    }

    clearSelection() {
        this.selectedItems = [];
        this.lastSelectedIndex = -1;
    }

    getRowClass(item: MedSyncQueueItem): string {
        const isSelected = this.selectedItems.some(selected => selected.enrollmentId === item.enrollmentId);
        return isSelected ? 'table-primary' : '';
    }

    // Bulk action methods
    exportToCsv() {
        if (!this.selectedItems.length) return;

        const headers = [
            'Patient ID',
            'Patient Name',
            'Anchor Date',
            'Days Until Anchor',
            'Medication Count',
            'Needs New Rx',
            'Note Count',
            'Contact Status',
            'Last Contact Date',
            'Priority',
        ];

        const rows = this.selectedItems.map(item => [
            item.patientId,
            item.patientName,
            this.formatDate(item.anchorDate),
            item.daysUntilAnchor,
            item.medicationCount,
            item.needsNewRxCount,
            item.noteCount,
            item.contactStatus,
            item.lastContactDate ? this.formatDate(item.lastContactDate) : 'Never',
            item.priority,
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `medsync-queue-export-${moment().format('YYYY-MM-DD')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.$notification(NotificationOptions.success(`Exported ${this.selectedItems.length} ${this.pluralize(this.selectedItems.length, 'patient')} to CSV`));
    }

    async generateCallList() {
        if (!this.selectedItems.length) return;

        // Fetch patient phone numbers for selected items
        const patientIds = this.selectedItems.map(item => item.patientId);

        try {
            const response = await Axios.post('/api/medsync/generate-call-list', {
                patientIds,
                enrollmentIds: this.selectedItems.map(item => item.enrollmentId),
            });

            const callListData = response.data;

            // Generate printable call list
            const printContent = this.generateCallListHtml(callListData);
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.write(printContent);
                printWindow.document.close();
                printWindow.print();
            }
        } catch (error) {
            console.error('Error generating call list:', error);
            this.$notification(NotificationOptions.error('Failed to generate call list'));
        }
    }

    private generateCallListHtml(callListData: any[]): string {
        const rows = callListData.map(item => `
            <tr>
                <td>${item.patientName}</td>
                <td>${item.phoneNumber || 'N/A'}</td>
                <td>${this.formatDate(item.anchorDate)}</td>
                <td>${item.medications?.join(', ') || 'N/A'}</td>
                <td style="width: 200px;"></td>
            </tr>
        `).join('');

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>MedSync Call List - ${moment().format('MM/DD/YYYY')}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    h1 { font-size: 18px; margin-bottom: 5px; }
                    h2 { font-size: 14px; color: #666; margin-top: 0; }
                    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
                    th { background-color: #f5f5f5; font-weight: bold; }
                    tr:nth-child(even) { background-color: #fafafa; }
                    @media print {
                        body { padding: 0; }
                        button { display: none; }
                    }
                </style>
            </head>
            <body>
                <h1>MedSync Call List</h1>
                <h2>Generated: ${moment().format('MM/DD/YYYY h:mm A')} | ${callListData.length} ${this.pluralize(callListData.length, 'patient')}</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Patient Name</th>
                            <th>Phone Number</th>
                            <th>Anchor Date</th>
                            <th>Medications</th>
                            <th>Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rows}
                    </tbody>
                </table>
            </body>
            </html>
        `;
    }

    showBulkContactModal() {
        this.bulkContactStatus = '';
        this.bulkContactNotes = '';
        this.$bvModal.show('bulk-contact-modal');
    }

    async saveBulkContact() {
        if (!this.bulkContactStatus || !this.selectedItems.length) return;

        try {
            const enrollmentIds = this.selectedItems.map(item => item.enrollmentId);

            await Axios.post('/api/medsync/bulk-contact', {
                enrollmentIds,
                contactStatus: this.bulkContactStatus,
                notes: this.bulkContactNotes,
            });

            this.$notification(NotificationOptions.success(`Logged contact attempt for ${enrollmentIds.length} ${this.pluralize(enrollmentIds.length, 'patient')}`));

            // Clear cached contact history for affected enrollments
            enrollmentIds.forEach(id => this.$delete(this.contactHistory, id));

            // Refresh queue and clear selection
            await this.loadQueue();
            this.clearSelection();
        } catch (error) {
            console.error('Error logging bulk contact:', error);
            this.$notification(NotificationOptions.error('Failed to log contact attempts'));
        }
    }

    showAssignModal() {
        this.selectedAssignee = new User();
        this.$bvModal.show('assign-modal');
    }

    async saveAssignment() {
        if (!this.selectedAssigneeUserName || !this.selectedItems.length) return;

        try {
            const enrollmentIds = this.selectedItems.map(item => item.enrollmentId);

            await Axios.post('/api/medsync/bulk-assign', {
                enrollmentIds,
                assignedTo: this.selectedAssigneeUserName,
            });

            this.$notification(NotificationOptions.success(`Assigned ${enrollmentIds.length} ${this.pluralize(enrollmentIds.length, 'patient')} to ${this.formatUserLabel(this.selectedAssignee)}`));

            // Refresh queue and clear selection
            await this.loadQueue();
            this.clearSelection();
        } catch (error) {
            console.error('Error assigning:', error);
            this.$notification(NotificationOptions.error('Failed to assign'));
        }
    }

    showBatchReminderModal() {
        this.reminderType = 'sms';
        this.$bvModal.show('batch-reminder-modal');
    }

    async sendBatchReminder() {
        if (!this.selectedItems.length) return;

        this.sendingReminder = true;
        try {
            const enrollmentIds = this.selectedItems.map(item => item.enrollmentId);

            const response = await Axios.post('/api/medsync/bulk-send-reminder', {
                enrollmentIds,
                reminderType: this.reminderType,
            });

            const result = response.data;
            this.$notification(NotificationOptions.success(
                `Sent ${result.successCount} ${this.pluralize(result.successCount, 'reminder')}. ${result.skippedCount > 0 ? `${result.skippedCount} skipped (no contact info).` : ''}`
            ));

            // Clear selection
            this.clearSelection();
        } catch (error) {
            console.error('Error sending batch reminders:', error);
            this.$notification(NotificationOptions.error('Failed to send batch reminders'));
        } finally {
            this.sendingReminder = false;
        }
    }
}
</script>

<style scoped>
#medsync-queue-container {
    padding: 20px;
}

#medsync-queue-container >>> tbody tr {
    cursor: pointer;
}

#medsync-queue-container >>> tbody tr.table-primary {
    background-color: rgba(0, 123, 255, 0.15) !important;
}

/* Consistent filter label styling */
.filter-label {
    font-weight: 600;
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
    display: block;
}

/* Ensure action buttons have consistent width */
.action-btn {
    min-width: 100px;
    text-align: center;
}

/* Make badges take full column width */
.badge-block {
    display: block;
    width: 100%;
    text-align: center;
}

/* Expand chevron animation */
.expand-chevron {
    cursor: pointer;
    transition: transform 0.2s ease-in-out;
    color: #6c757d;
}

.expand-chevron:hover {
    color: #495057;
}

.expand-chevron.expanded {
    transform: rotate(90deg);
}
</style>
